import { useState, useCallback } from 'react';
import { WizardData } from '../components/AIActivityWizard';
import { Database } from '../lib/supabase';
import { CurriculumSelection } from '../lib/curriculum-systems';

type Child = Database['public']['Tables']['children']['Row'];

interface UseWizardStateOptions {
  mode: 'new-child' | 'existing-child';
  selectedChild?: Child;
}

const DEFAULT_WIZARD_DATA: WizardData = {
  mode: 'new-child',
  learningProfile: {
    curriculumTargets: [],
    learningObjectives: [],
    customInstructions: '',
    accommodations: [],
  },
  activityPlan: {
    subjects: [],
    totalActivities: 0,
    difficulty: 'medium',
  },
  generatedActivities: [],
};

export function useWizardState(options: UseWizardStateOptions) {
  const [wizardData, setWizardData] = useState<WizardData>({
    ...DEFAULT_WIZARD_DATA,
    mode: options.mode,
    selectedChild: options.selectedChild,
  });

  const updateWizardData = useCallback((updates: Partial<WizardData>) => {
    setWizardData(prev => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetWizard = useCallback(() => {
    setWizardData(DEFAULT_WIZARD_DATA);
  }, []);

  const canProceedToNext = useCallback((stepId: string): boolean => {
    switch (stepId) {
      case 'welcome':
        return wizardData.mode !== undefined;
      
      case 'child-selection':
        if (wizardData.mode === 'new-child') {
          return !!(
            wizardData.childData?.name &&
            wizardData.childData?.age &&
            wizardData.childData?.avatar_color
          );
        }
        return !!wizardData.selectedChild;
      
      case 'curriculum-selection':
        return !!(
          wizardData.curriculumSelection?.systemId &&
          wizardData.curriculumSelection?.levelId
        );
      
      case 'learning-profile':
        // Learning profile is optional, but we need at least some data
        return true;
      
      case 'activity-plan':
        return wizardData.activityPlan.subjects.length > 0 && 
               wizardData.activityPlan.totalActivities > 0;
      
      case 'generation':
        // Can't proceed during generation
        return false;
      
      case 'completion':
        return true;
      
      default:
        return false;
    }
  }, [wizardData]);

  const getCurrentChild = useCallback((): Child | undefined => {
    return wizardData.createdChild || wizardData.selectedChild;
  }, [wizardData.createdChild, wizardData.selectedChild]);

  const getTotalSteps = useCallback((): number => {
    // Total steps: Welcome, Child Selection, Curriculum Selection, Learning Profile, Activity Plan, Generation, Completion = 7
    // Skip child selection step if we already have a selected child
    return wizardData.mode === 'existing-child' && wizardData.selectedChild ? 6 : 7;
  }, [wizardData.mode, wizardData.selectedChild]);

  const getCompletedActivitiesCount = useCallback((): number => {
    return wizardData.generatedActivities.filter(activity => 
      activity.status === 'completed'
    ).length;
  }, [wizardData.generatedActivities]);

  const getFailedActivitiesCount = useCallback((): number => {
    return wizardData.generatedActivities.filter(activity => 
      activity.status === 'error'
    ).length;
  }, [wizardData.generatedActivities]);

  const isGenerationComplete = useCallback((): boolean => {
    const totalActivities = wizardData.activityPlan.totalActivities;
    const processedActivities = wizardData.generatedActivities.filter(activity =>
      activity.status === 'completed' || activity.status === 'error'
    ).length;
    
    return totalActivities > 0 && processedActivities === totalActivities;
  }, [wizardData.activityPlan.totalActivities, wizardData.generatedActivities]);

  return {
    wizardData,
    updateWizardData,
    resetWizard,
    canProceedToNext,
    getCurrentChild,
    getTotalSteps,
    getCompletedActivitiesCount,
    getFailedActivitiesCount,
    isGenerationComplete,
  };
}